import type { User } from "@/shared/types/auth.types";
import type { PermissionContext } from "./auth/permission.types";
import { RoleResolver } from "./auth/roleResolver";
import { PermissionEvaluator } from "./auth/permissionEvaluator";

// Permission service - Orchestrates role resolution and permission evaluation
export class PermissionService {
  private static instance: PermissionService;
  private roleResolver = RoleResolver.getInstance();
  private permissionEvaluator = PermissionEvaluator.getInstance();

  static getInstance(): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService();
    }
    return PermissionService.instance;
  }

  // Check if user can perform action on resource with context
  hasPermission(
    user: User,
    permission: string,
    context?: PermissionContext,
  ): boolean {
    const userPermissions = this.roleResolver.getUserPermissions(user);
    const evaluation = this.permissionEvaluator.canPerformAction(
      userPermissions,
      permission,
      user,
      context,
    );

    return evaluation.granted;
  }

  // Get all permissions for user including inherited ones
  getUserPermissions(user: User): string[] {
    return this.roleResolver.getUserPermissions(user);
  }

  // Check if user has specific role
  hasRole(user: User, role: string): boolean {
    return this.roleResolver.hasRole(user, role);
  }

  // Check if user has any of the specified roles
  hasAnyRole(user: User, roles: string[]): boolean {
    return this.roleResolver.hasAnyRole(user, roles);
  }

  // Get user's effective roles (including inherited)
  getEffectiveRoles(user: User): string[] {
    return this.roleResolver.getUserRoles(user);
  }
}
